import {action, makeAutoObservable, toJS} from "mobx";
import {inject, injectable} from "inversify";
import type {IStandingsStore, IUserStanding} from "data/stores/standings/standings.store";
import {Bindings} from "data/constants/bindings";
import type {IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {ITournament, ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import {
	chain,
	concat,
	flatMap,
	groupBy,
	identity,
	includes,
	intersection,
	keys,
	max,
	mean,
	orderBy,
	round,
	sum,
	toNumber,
	uniq,
	xor,
} from "lodash";
import {TournamentStatus} from "data/enums";
import type {IHeavyPredictionsStore} from "data/stores/heavy_predictions/heavy_predictions.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

export interface IOrderedStandingsStore {
	getSortedStandings: (standings: IUserStanding[], conferenceId: number) => IUserStanding[];
}

@injectable()
export class OrderedStandingsStore implements IOrderedStandingsStore {
	constructor(
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.TournamentsStore) private _tournamentStore: ITournamentsStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.HeavyPredictionsStore) private _hpStore: IHeavyPredictionsStore
	) {
		makeAutoObservable(this);
	}

	private getOrderedKeys = (obj: Record<string, IUserStanding[]>) => {
		return orderBy(keys(obj).map(toNumber), identity, ["desc"]);
	};

	private getUpdatedStandings = (standings: IUserStanding[]) => {
		return standings.map((it) => {
			const squad = this._squadStore.getSquadById(it.squadId);
			return {
				...it,
				divisionId: squad?.divisionId ?? 0,
				conferenceId: squad?.conferenceId ?? 0,
			};
		});
	};

	private getSovSquadsOld = (standing: IUserStanding) => {
		return (
			max(
				uniq(
					this._tournamentStore.tournaments
						.filter(
							(it) =>
								it.status === TournamentStatus.Complete &&
								includes([it.homeSquadId, it.awaySquadId], standing.squadId)
						)
						.flatMap((it) => [it.homeSquadId, it.awaySquadId])
						// Check logic for filter squads without lose & tie fields
						.filter((squadId) => squadId !== standing.squadId)
				).map(
					(opponentSquadId) =>
						this._standingsStore.standingsBySquadId(opponentSquadId)
							?.commonWinPercent ?? 0
				)
			) || 0
		);
	};

	private getSovSquads = (standing: IUserStanding) => {
		const squadData = {
			win: 0,
			loose: 0,
		};

		const completeMatches = this._tournamentStore.tournaments.filter(
			(it) => it.status === TournamentStatus.Complete
		);
		const predictedMatchIds = chain(flatMap(this._hpStore.predictions))
			// .filter((it) => it.squadId === standing.squadId)
			.map((it) => it.matchId)
			.value();
		const predictedMatches = this._tournamentStore.tournaments.filter(
			(it) =>
				includes([TournamentStatus.Scheduled, TournamentStatus.Playing], it.status) &&
				includes(predictedMatchIds, it.id)
		);

		const matchesWithSquad = [...completeMatches, ...predictedMatches].filter((it) =>
			includes([it.homeSquadId, it.awaySquadId], standing.squadId)
		);

		const winnerMatches = matchesWithSquad.filter((it) => {
			const isHomeSquad = standing.squadId === it.homeSquadId;
			return isHomeSquad
				? Number(it.homeSquadScore) > Number(it.awaySquadScore)
				: Number(it.homeSquadScore) < Number(it.awaySquadScore);
		});

		winnerMatches.forEach((el) => {
			const isHomeSquad = standing.squadId === el.homeSquadId;
			const opponentSquadId = isHomeSquad ? el.awaySquadId : el.homeSquadId;
			const opponentStanding = this._standingsStore.lastCompleteStandingsBySquadId(
				opponentSquadId,
				this._roundStore.actualStandingsRoundId
			);

			if (!opponentStanding) return;

			squadData.win += opponentStanding.wins;
			squadData.loose += opponentStanding.losses;
		});

		return round((squadData.win / (squadData.win + squadData.loose)) * 1000);
	};

	private getSosSquads = (standing: IUserStanding) => {
		return (
			mean(
				uniq(
					this._tournamentStore.tournaments
						.filter(
							(it) =>
								it.status === TournamentStatus.Complete &&
								includes([it.homeSquadId, it.awaySquadId], standing.squadId)
						)
						.flatMap((it) => [it.homeSquadId, it.awaySquadId])
						// Check logic for filter squads without lose & tie fields
						.filter((squadId) => squadId !== standing.squadId)
				).map(
					(opponentSquadId) =>
						this._standingsStore.standingsBySquadId(opponentSquadId)
							?.commonWinPercent ?? 0
				)
			) || 0
		);
	};

	private squadMatchesForH2HOrder = (
		standingsArr: IUserStanding[],
		currentStanding: IUserStanding
	) => {
		const possibleOpponentsIds = standingsArr
			.filter((it) => it.squadId !== currentStanding.squadId)
			.map((it) => it.squadId);

		return this._tournamentStore.tournaments.filter(
			(it) =>
				it.status === TournamentStatus.Complete &&
				includes([it.homeSquadId, it.awaySquadId], currentStanding.squadId) &&
				intersection(possibleOpponentsIds, [it.homeSquadId, it.awaySquadId]).length
		);
	};

	private squadMatchesForH2HUserOrder = (
		standingsArr: IUserStanding[],
		currentStanding: IUserStanding
	) => {
		const possibleOpponentsIds = standingsArr
			.filter((it) => it.squadId !== currentStanding.squadId)
			.map((it) => it.squadId);

		const predictedMatchIds = chain(flatMap(this._hpStore.predictions))
			.filter((it) => it.squadId === currentStanding.squadId)
			.map((it) => it.matchId)
			.value();

		const completeMatches = this._tournamentStore.tournaments.filter(
			(it) =>
				it.status === TournamentStatus.Complete &&
				includes([it.homeSquadId, it.awaySquadId], currentStanding.squadId) &&
				intersection(possibleOpponentsIds, [it.homeSquadId, it.awaySquadId]).length
		);

		const scheduledPredictedMatches = this._tournamentStore.tournaments.filter(
			(it) =>
				includes([TournamentStatus.Scheduled, TournamentStatus.Playing], it.status) &&
				includes(predictedMatchIds, it.id) &&
				intersection(possibleOpponentsIds, [it.homeSquadId, it.awaySquadId]).length
		);

		const allMatches = concat(completeMatches, scheduledPredictedMatches);

		return allMatches.filter(
			(it) =>
				includes([it.homeSquadId, it.awaySquadId], currentStanding.squadId) &&
				intersection(possibleOpponentsIds, [it.homeSquadId, it.awaySquadId]).length
		);
	};

	private getH2HWinPercent = (squadMatches: ITournament[], standing: IUserStanding) => {
		return sum(
			squadMatches.map((it) => {
				const isHomeSquad = it.homeSquadId === standing.squadId;

				return Number(
					isHomeSquad
						? Number(it.homeSquadScore) > Number(it.awaySquadScore)
						: Number(it.awaySquadScore) > Number(it.homeSquadScore)
				);
			})
		);
	};

	private orderSquadsByDivision = (squadsRankingTable: IUserStanding[], conferenceId: number) => {
		let divisionIDs = this._divisionStore.divisionsIdsByConferenceId(conferenceId);
		let insertIndex = 0;

		squadsRankingTable.forEach((standing, index) => {
			const squad = this._squadStore.getSquadById(standing.squadId);

			if (!squad) return;

			if (divisionIDs.includes(squad.divisionId)) {
				divisionIDs = xor(divisionIDs, [squad.divisionId]);

				if (index) {
					const removedSquad = squadsRankingTable.splice(index, 1)[0];
					squadsRankingTable.splice(insertIndex, 0, removedSquad);
				}

				insertIndex++;
			}
		});

		return squadsRankingTable;
	};

	private sortDivisionsWinnersStandings = (
		standings: IUserStanding[],
		topStandings: IUserStanding[]
	) => {
		const groupsByDivisions = groupBy(this.getUpdatedStandings(standings), "divisionId");
		const resultedArr: IUserStanding[] = [];

		this.getOrderedKeys(groupsByDivisions).forEach((key) => {
			const standingsChunk = orderBy(
				groupsByDivisions[key],
				["commonWinPercent", "divisionWinPercent", (it) => this.getSovSquads(it)],
				["desc", "desc", "desc"]
			);
			resultedArr.push(standingsChunk[0]);
		});

		this.fillSquadRanksArray(resultedArr, topStandings);
	};

	private sortRestStandings = (standings: IUserStanding[], restStandings: IUserStanding[]) => {
		this.fillWildCardRanksArray(standings, restStandings);
	};

	//Dear developer, only GOD knows how this code should work with this documentation.
	// Link to Confluence - https://geniussports.atlassian.net/wiki/spaces/NFLFH/pages/4615210628/Heavy+Touch+Algorithm
	@action getSortedStandings = (standings: IUserStanding[], conferenceId: number) => {
		const topStandings: IUserStanding[] = [];
		const restStandings: IUserStanding[] = [];

		// 1-4 slots Winners divisions
		this.sortDivisionsWinnersStandings(standings, topStandings);

		// 5-16 slots rest standings
		const restArr = standings.filter(
			(it) =>
				!includes(
					topStandings.map((el) => el.squadId),
					it.squadId
				)
		);

		this.sortRestStandings(restArr, restStandings);

		return concat(topStandings, restStandings);
	};

	private applyGroupAction = (
		group: Record<string, IUserStanding[]>,
		newStandings: IUserStanding[],
		callback: (standingsChunk: IUserStanding[], newStandings: IUserStanding[]) => void
	) => {
		this.getOrderedKeys(group).forEach((key) => {
			const standingsChunk = group[key];

			if (standingsChunk.length === 1) {
				newStandings.push(...standingsChunk);
				return;
			}

			callback(standingsChunk, newStandings);
		});
	};

	private fillSquadRanksArray = (standings: IUserStanding[], newStandings: IUserStanding[]) => {
		const group = groupBy(standings, "commonWinPercent");

		this.applyGroupAction(group, newStandings, this.h2hTieBreaker);
	};

	private fillWildCardRanksArray = (
		standings: IUserStanding[],
		newStandings: IUserStanding[]
	) => {
		const group = groupBy(standings, "commonWinPercent");

		this.getOrderedKeys(group).forEach((key) => {
			const standingsChunk = group[key];

			if (standingsChunk.length === 1) {
				newStandings.push(...standingsChunk);
				return;
			}

			this.applyWildCardTieBreakers(standingsChunk, newStandings);
		});
	};

	private applyWildCardTieBreakers = (
		standingsChunk: IUserStanding[],
		newStandings: IUserStanding[]
	): void => {
		const tempArr = toJS(standingsChunk);

		if (tempArr.length === 1) {
			newStandings.push(...tempArr);
			return;
		}

		//H2H tieBreaker
		for (let i = 0; i < tempArr.length; i++) {
			const squadMatches = this.squadMatchesForH2HUserOrder(tempArr, tempArr[i]);
			const h2hMatchCount = this.getH2HWinPercent(squadMatches, tempArr[i]);

			if (h2hMatchCount === tempArr.length - 1) {
				newStandings.push(tempArr[i]);
				const newTempArr = tempArr.filter((it) => it.squadId !== tempArr[i].squadId);

				return this.applyWildCardTieBreakers(newTempArr, newStandings);
			}
		}

		//Conference win percent tieBreaker
		const confGroup = groupBy(tempArr, "conferenceWinPercent");
		const confSortedGroup = this.getOrderedKeys(confGroup);

		const confStandingChunk = confGroup[confSortedGroup[0]];

		if (confStandingChunk.length === 1) {
			newStandings.push(confStandingChunk[0]);
			const newTempArr = tempArr.filter((it) => it.squadId !== confStandingChunk[0].squadId);

			return this.applyWildCardTieBreakers(newTempArr, newStandings);
		}

		//SOV tieBreaker
		const sovGroup = {} as Record<number, IUserStanding[]>;

		tempArr.forEach((standing) => {
			const sovSquad = this.getSovSquads(standing);

			// console.log(standing.squadId, "---", sovSquad);

			sovGroup[sovSquad] = sovGroup[sovSquad] || [];
			sovGroup[sovSquad].push(standing);
		});

		const sovSortedGroup = this.getOrderedKeys(sovGroup);
		const sovStandingChunk = sovGroup[sovSortedGroup[0]];

		if (sovStandingChunk.length === 1) {
			newStandings.push(sovStandingChunk[0]);
			const newTempArr = tempArr.filter((it) => it.squadId !== sovStandingChunk[0].squadId);

			return this.applyWildCardTieBreakers(newTempArr, newStandings);
		}

		//SOS tieBreaker
		const sosGroup = {} as Record<number, IUserStanding[]>;

		tempArr.forEach((standing) => {
			const sosSquad = this.getSosSquads(standing);

			sosGroup[sosSquad] = sosGroup[sosSquad] || [];
			sosGroup[sosSquad].push(standing);
		});

		const sosSortedGroup = this.getOrderedKeys(sosGroup);
		const sosStandingChunk = sosGroup[sosSortedGroup[0]];

		if (sosStandingChunk.length === 1) {
			newStandings.push(sosStandingChunk[0]);
			const newTempArr = tempArr.filter((it) => it.squadId !== sosStandingChunk[0].squadId);

			return this.applyWildCardTieBreakers(newTempArr, newStandings);
		}

		//Points tieBreaker
		const pointsChunk = orderBy(tempArr, (it) => it.pointsFor - it.pointsAgainst, ["desc"]);

		newStandings.push(...pointsChunk);
	};

	private h2hTieBreaker = (standingsChunk: IUserStanding[], newStandings: IUserStanding[]) => {
		const group = {} as Record<string, IUserStanding[]>;

		standingsChunk.forEach((standing) => {
			const squadMatches = this.squadMatchesForH2HUserOrder(standingsChunk, standing);
			const h2hWinPercent = this.getH2HWinPercent(squadMatches, standing);

			group[h2hWinPercent] = group[h2hWinPercent] || [];
			group[h2hWinPercent].push(standing);
		});

		// this.applyGroupAction(group, newStandings, this.divisionTieBreaker);
		this.applyGroupAction(group, newStandings, this.conferenceTieBreaker);
	};

	private wildCardH2hTieBreaker = (
		standingsChunk: IUserStanding[],
		newStandings: IUserStanding[]
	) => {
		const group = {} as Record<string, IUserStanding[]>;

		standingsChunk.forEach((standing) => {
			const squadMatches = this.squadMatchesForH2HOrder(standingsChunk, standing);
			const h2hWinPercent = this.getH2HWinPercent(squadMatches, standing);

			group[h2hWinPercent] = group[h2hWinPercent] || [];
			group[h2hWinPercent].push(standing);
		});

		this.applyGroupAction(group, newStandings, this.sovTieBreaker);
	};

	private divisionWinsTieBreaker = (
		standingsChunk: IUserStanding[],
		newStandings: IUserStanding[]
	) => {
		const group = groupBy(standingsChunk, "divisionWinPercent");

		this.applyGroupAction(group, newStandings, this.sovTieBreaker);
	};

	private conferenceTieBreaker = (
		standingsChunk: IUserStanding[],
		newStandings: IUserStanding[]
	) => {
		const group = groupBy(standingsChunk, "conferenceWinPercent");

		this.applyGroupAction(group, newStandings, this.sovTieBreaker);
	};

	private wildCardConferenceTieBreaker = (
		standingsChunk: IUserStanding[],
		newStandings: IUserStanding[]
	) => {
		const group = groupBy(standingsChunk, "conferenceWinPercent");

		this.applyGroupAction(group, newStandings, this.wildCardH2hTieBreaker);
	};

	private sovTieBreaker = (standingsChunk: IUserStanding[], newStandings: IUserStanding[]) => {
		const group = {} as Record<number, IUserStanding[]>;

		standingsChunk.forEach((standing) => {
			const sovSquad = this.getSovSquads(standing);

			group[sovSquad] = group[sovSquad] || [];
			group[sovSquad].push(standing);
		});

		this.applyGroupAction(group, newStandings, this.sosTieBreaker);
	};

	private sosTieBreaker = (standingsChunk: IUserStanding[], newStandings: IUserStanding[]) => {
		const group = {} as Record<number, IUserStanding[]>;

		standingsChunk.forEach((standing) => {
			const sosSquad = this.getSosSquads(standing);

			group[sosSquad] = group[sosSquad] || [];
			group[sosSquad].push(standing);
		});

		// this.getOrderedKeys(group).forEach((key) => {
		// 	const sosBySquad = group[key];
		// 	newStandings.push(...sosBySquad);
		// });

		// this.applyGroupAction(group, newStandings, this.divisionWinsTieBreaker);
		this.applyGroupAction(group, newStandings, this.pointsTieBreaker);
	};

	private pointsTieBreaker = (standingsChunk: IUserStanding[], newStandings: IUserStanding[]) => {
		const orderChunk = orderBy(standingsChunk, (it) => it.pointsFor - it.pointsAgainst, [
			"desc",
		]);

		newStandings.push(...orderChunk);
	};
}
